import React, { useEffect } from 'react';
import { BottomSheet } from 'react-spring-bottom-sheet';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import * as Icon from 'react-feather';
import Marquee from '../../helpers/marquee.component';
import { connect } from 'react-redux';

/**
 * ModalSheet component renders a customizable modal bottom sheet.
 *
 * @param {boolean} isOpen - Whether to open the modal or not.
 * @param {string} [title] - Title of the modal. Mandatory, if you want to use the close icon and the refresh button.
 * @param {function} closeModal - Function that closes the modal.
 * @param {React.ReactNode} [footer] - Footer content of the modal (if any).
 * @param {boolean} [isRefreshing] - Whether to make the refresh icon rotate or not.
 * @param {function} [onRefreshHandler] - Function that gets triggered when the refresh icon is clicked.
 * @param {function} [onOpen] - Function that gets triggered before the Modal is opened.
 * @param {function} [onClose] - Function that gets triggered when Modal is closed, but before unmount.
 * @param {Object} [marqueeProps] - Props for the Marquee in title
 * @param {boolean} [disableEnterToSubmit] - Whether to disable the onKeyDown for Enter or not
 * @param {Object} [children] - The children
 * @param {Object} [props] - Override BottomSheet's properties (see: https://github.com/stipsan/react-spring-bottom-sheet).
 *
 * @returns {ReactElement} The ModalSheet component.
 */
const ModalSheet = ({
  isOpen,
  title,
  closeModal,
  footer,
  isRefreshing,
  onRefreshHandler,
  onOpen,
  onClose,
  marqueeProps,
  disableEnterToSubmit,
  children,
  disableContentDrag,
  kiosk,
  ...props
}) => {
  // Recursive function to find the first clickable element
  const findClickableElement = (element) => {
    if (!element || typeof element !== 'object') return null;
    if (element.props && element.props.onClick) return element;
    if (element.props && element.props.children) {
      const childrenArray = Array.isArray(element.props.children)
        ? element.props.children
        : [element.props.children];
      for (let child of childrenArray) {
        const clickableChild = findClickableElement(child);
        if (clickableChild) return clickableChild;
      }
    }
    return null;
  };

  // TODO: this is risky on mobile!!!
  useEffect(() => {
    if (!disableEnterToSubmit) {
      const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
          const clickableElement = findClickableElement(footer);
          if (clickableElement && clickableElement.props.onClick) {
            clickableElement.props.onClick();
          }
        }
      };

      if (isOpen) {
        window.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, footer]);

  return (
    <BottomSheet
      open={isOpen}
      initialFocusRef={false}
      enableTouchThrough={false}
      expandOnContentDrag={(disableContentDrag || kiosk) ? false : true}
      scrollLocking={true}
      onDismiss={closeModal}
      onSpringCancel={
        ({ type }) => type === 'CLOSE' && onClose && onClose() // this avoids that stale data is persisted in Modal
      }
      onSpringStart={({ type }) => type === 'OPEN' && onOpen && onOpen()}
      onSpringEnd={({ type }) => type === 'CLOSE' && onClose && onClose()}
      maxHeight={window.innerHeight * 0.95}
      sibling={
        <div
          data-rsbs-backdrop="true"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            closeModal();
          }}
        />
      }
      blocking={false}
      header={
        title ? (
          <div className="default-header-container d-flex align-items-center">
            <div
              className="primary-action-container px-1 py-75 flex-grow-0"
              onClick={closeModal}
            >
              <Icon.X
                size={22}
                className="danger"
                data-testid="closeModalButton"
              />
            </div>
            <h1 className="title-container font-medium-1 m-0 text-center flex-grow-1">
              <Marquee {...marqueeProps}>{title}</Marquee>
            </h1>
            <div
              className="secondary-action-container px-1 py-75 flex-grow-0"
              onClick={() =>
                onRefreshHandler && !isRefreshing && onRefreshHandler()
              }
            >
              {onRefreshHandler ? (
                // render the icon only if onRefreshHandler exists
                <Icon.RefreshCcw
                  size={20}
                  className={classnames('success', {
                    rotate360: isRefreshing,
                  })}
                />
              ) : (
                // dummy element to keep spaces the same
                <div style={{ width: '20px' }} />
              )}
            </div>
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-center p-50">
            <div className="header-draggable" />
          </div>
        )
      }
      footer={footer}
      {...props}
    >
      <div className={classnames({ 'pb-safe': !footer })}>{children}</div>
    </BottomSheet>
  );
};

const mapStateToProps = (state) => ({
  kiosk: state.store.kiosk,
});

export default connect(mapStateToProps, {
})(ModalSheet);
